import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Icon from "../components/Icon";
import { useTheme } from "../theme/ThemeProvider";
import { Button, Field, IconButton, Input } from "../ui";
import { BrandMark, useBrand } from "../brand/BrandProvider";

/** Что система делает — коротко, чтобы вход не был пустым экраном. */
const HIGHLIGHTS = [
  {
    icon: "sparkles" as const,
    title: "Агенты ведут рутину",
    text: "Приём заявки, категория, письма поставщикам и сверка счетов — без ручного ввода.",
  },
  {
    icon: "route" as const,
    title: "Маршруты под правила компании",
    text: "Согласование собирается по сумме, категории и подразделению.",
  },
  {
    icon: "history" as const,
    title: "Каждое решение объяснимо",
    text: "Кто, когда и почему — в неизменяемом аудит-логе.",
  },
];

/** Куда вести после входа: у поставщика и склада свои экраны. */
function homeFor(role: string | undefined): string {
  if (role === "supplier") return "/portal";
  if (role === "warehouse") return "/warehouse";
  return "/";
}

export default function LoginPage() {
  const brand = useBrand();
  const { login, user } = useAuth();
  const nav = useNavigate();
  const { theme, toggle } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (user) {
    nav(homeFor(user.role), { replace: true });
    return null;
  }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const account = await login(email, password);
      nav(homeFor(account.role), { replace: true });
    } catch (err) {
      const raw = err instanceof Error ? err.message : String(err);
      let text = raw;
      try {
        const parsed = JSON.parse(raw) as { detail?: unknown };
        if (typeof parsed.detail === "string") text = parsed.detail;
      } catch {
        /* не JSON — показываем как есть */
      }
      setError(text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-canvas">
      {/* ---------- форма ---------- */}
      <div className="flex w-full flex-col justify-center px-5 py-10 sm:px-10 lg:w-[30rem] lg:shrink-0 lg:border-r lg:border-line lg:bg-surface">
        <div className="absolute right-4 top-4">
          <IconButton
            icon={theme === "dark" ? "moon" : "sun"}
            label="Сменить тему"
            onClick={toggle}
          />
        </div>

        <div className="mx-auto w-full max-w-sm">
          <div className="flex items-center gap-2.5">
            <BrandMark size="lg" />
            <div>
              <div className="text-sm font-semibold leading-tight text-ink" data-testid="brand-login-name">{brand.name}</div>
              <div className="text-2xs leading-tight text-faint">{brand.tagline}</div>
            </div>
          </div>

          <h1 className="mt-8 text-2xl font-semibold text-ink">Вход в систему</h1>
          <p className="mt-1.5 text-sm text-muted">
            Используйте корпоративную учётную запись.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {/* Не type="email": сотрудники входят и коротким доменным логином
                («jaguarmw»), если включён вход через LDAP. */}
            <Field label="Email или логин" htmlFor="login-email">
              <Input
                id="login-email"
                type="text"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoFocus
                required
                placeholder="name@company.ru"
              />
            </Field>

            <Field label="Пароль" htmlFor="login-password">
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                placeholder="••••••••"
              />
            </Field>

            {error && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-danger-line bg-danger-soft px-3 py-2 text-sm text-danger-ink"
              >
                <Icon name="alert" size={15} className="mt-px shrink-0" />
                <span className="leading-snug">{error}</span>
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              Войти
            </Button>
          </form>

          <div className="mt-3 text-center">
            <Link to="/forgot-password" className="text-sm text-muted transition-colors hover:text-ink">
              Забыли пароль?
            </Link>
          </div>

          <div className="mt-6 border-t border-line pt-5 text-center">
            <Link
              to="/portal/register"
              className="inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-ink"
            >
              <Icon name="building" size={14} />
              Регистрация для поставщиков
              <Icon name="arrowRight" size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* ---------- смысловая половина ---------- */}
      <div className="relative hidden flex-1 items-center justify-center overflow-hidden p-12 lg:flex">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.55] dark:opacity-40"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(var(--c-line-strong)) 1px, transparent 0)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 80% 70% at 50% 45%, black, transparent)",
          }}
        />
        <div className="relative max-w-md">
          <h2 className="text-2xl font-semibold leading-snug text-ink">
            Закупка от заявки до оплаты — в одном потоке
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-muted">
            Система ведёт процесс сама и передаёт человеку только те решения,
            которые требуют его полномочий.
          </p>

          <ul className="mt-8 space-y-5">
            {HIGHLIGHTS.map((item) => (
              <li key={item.title} className="flex gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-muted shadow-xs">
                  <Icon name={item.icon} size={16} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">{item.title}</div>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted">{item.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
